import { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, X, AlertTriangle, Loader2 } from "lucide-react";
import {
  barColor,
  compositeScore,
  deriveRiskFlags,
  isComplete,
  saveReview,
} from "../reviews";
import type {
  AnswersMap,
  Perspective,
  ReviewConfig,
  ReviewItem,
} from "../reviews";
import { useReviewAssist } from "../hooks/useReviewAssist";
import { ConfirmDialog } from "./ConfirmDialog";

// ---------- Prefill from the initiative ----------
//
// Loosely typed so this drops in regardless of the InitiativeItem shape.
// The extra fields (beyond id/title/description) exist so the "AI Review"
// button has the initiative's full record to work from, not just the title
// and problem statement.
export interface InitiativePrefill {
  id: number;
  title?: string;
  description?: string;
  submittedBy?: string;
  businessSponsor?: string;
  /** ISO date/datetime the initiative was submitted. Not currently used by
   *  this modal (AI Review is gated on review status, not age) — kept on
   *  the prefill in case a future feature needs it. */
  created?: string;
  team?: string;
  region?: string;
  problemFrequency?: string;
  impactedToday?: string;
  ifSolved?: string;
  anticipatedImpact?: string;
  impactType?: string;
  proposedSolution?: string;
  existingExamples?: string;
  dataInputs?: string;
  constraintsRisks?: string;
  anythingElse?: string;
}

/** Flattens the prefill into label/value pairs for the AI review prompt —
 *  every field captured on the initiative, not just title/description. */
function initiativeToFields(initiative: InitiativePrefill): { label: string; value: string }[] {
  const pairs: [string, string | undefined][] = [
    ["Title", initiative.title],
    ["Team / function", initiative.team],
    ["Region / division", initiative.region],
    ["Problem", initiative.description],
    ["How often it happens", initiative.problemFrequency],
    ["Who's impacted", initiative.impactedToday],
    ["What success looks like", initiative.ifSolved],
    ["Anticipated impact", initiative.anticipatedImpact],
    ["Type of impact", initiative.impactType],
    ["Proposed solution", initiative.proposedSolution],
    ["Existing examples", initiative.existingExamples],
    ["Data/inputs involved", initiative.dataInputs],
    ["Constraints/risks", initiative.constraintsRisks],
    ["Sponsor", initiative.businessSponsor],
    ["Anything else", initiative.anythingElse],
  ];
  return pairs
    .filter(([, value]) => value != null && value !== "")
    .map(([label, value]) => ({ label, value: value! }));
}

interface Props {
  initiative: InitiativePrefill;
  perspective: Perspective;
  /** Existing Current review to preload (admin re-edit). */
  existing?: ReviewItem | null;
  /** Runtime rubric loaded from the PILLARS + QUESTIONS SharePoint lists. */
  config: ReviewConfig;
  getToken: () => Promise<string>;
  reviewerUpn: string;
  reviewerName: string;
  onClose: () => void;
  onSaved: (review: ReviewItem) => void;
}

export function ReviewModal({
  initiative,
  perspective,
  existing,
  config,
  getToken,
  reviewerUpn,
  reviewerName,
  onClose,
  onSaved,
}: Props) {
  const cfg = config[perspective];

  // ---------- Meta fields ----------
  const today = new Date().toISOString().slice(0, 10);
  const [meta, setMeta] = useState<Record<string, string>>(() => ({
    InitiativeName: initiative.title ?? "",
    ReviewDate: existing?.reviewDate || today,
    Description: initiative.description ?? "",
    BusinessDescription: initiative.description ?? "",
    BusinessSponsor: initiative.businessSponsor ?? "",
    ReviewedBy: existing?.reviewerName || reviewerName || "",
  }));

  // ---------- Answers ----------
  const [answers, setAnswers] = useState<AnswersMap>(() => existing?.answers ?? {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  // ---------- AI Review ----------
  // Only offered before this perspective has a real score on file — once a
  // review is actually scored (composite > 0), this becomes a draft-assist
  // tool for fresh reviews, not a way to silently re-score something a
  // human already evaluated.
  const { scoreReview } = useReviewAssist();
  const [aiReviewing, setAiReviewing] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [confirmAiOverwrite, setConfirmAiOverwrite] = useState(false);
  const showAiReview = !existing || existing.composite <= 0;

  const allQuestions = useMemo(
    () => [...cfg.pillars.flatMap((p) => p.questions), ...cfg.extras],
    [cfg]
  );

  const runAiReview = async () => {
    setAiError(null);
    setAiReviewing(true);
    try {
      const result = await scoreReview(perspective, initiativeToFields(initiative), allQuestions);
      setAnswers((prev) => {
        const next = { ...prev };
        for (const q of allQuestions) {
          const ai = result[q.key];
          if (ai) next[q.key] = { score: ai.score, note: ai.note };
        }
        return next;
      });
    } catch (err) {
      setAiError(err instanceof Error ? err.message : String(err));
    } finally {
      setAiReviewing(false);
    }
  };

  const handleAiReviewClick = () => {
    const hasExistingAnswers = Object.values(answers).some((a) => !!a?.score);
    if (hasExistingAnswers) {
      setConfirmAiOverwrite(true);
      return;
    }
    void runAiReview();
  };

  const bodyRef = useRef<HTMLDivElement>(null);

  // Lock body scroll + Escape to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, saving]);

  const setScore = (key: string, score: number) =>
    setAnswers((a) => ({ ...a, [key]: { ...a[key], score } }));
  const setNote = (key: string, note: string) =>
    setAnswers((a) => ({ ...a, [key]: { score: a[key]?.score ?? 0, note } }));

  const r1Key = cfg.extras[0]?.key;
  const r1Overlap = r1Key ? answers[r1Key]?.score : undefined;

  const score = useMemo(() => compositeScore(cfg, answers), [cfg, answers]);
  const complete = useMemo(() => isComplete(cfg, answers), [cfg, answers]);
  const flags = useMemo(() => deriveRiskFlags(cfg, answers), [cfg, answers]);

  const accent = perspective === "Business" ? "Business" : "Technical";

  async function handleSave() {
    if (!complete) {
      setShowErrors(true);
      const first = document.querySelector(".q-block.v-error");
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const saved = await saveReview(
        token,
        {
          initiativeId: initiative.id,
          perspective,
          answers,
          reviewerUpn,
          reviewerName: meta.ReviewedBy || reviewerName,
          reviewDate: meta.ReviewDate || today,
          r1Overlap: r1Overlap,
        },
        config,
      );
      onSaved(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the review. Please try again.");
      setSaving(false);
    }
  }

  return (
    <div className="rv-backdrop" role="dialog" aria-modal="true" onClick={saving ? undefined : onClose}>
      <div className="rv-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`rv-head rv-head--${accent.toLowerCase()}`}>
          <div className="rv-head-text">
            <span className="rv-kind">{accent} Review</span>
            <h3 className="rv-title">{initiative.title || `Initiative #${initiative.id}`}</h3>
          </div>
          <span
            className="rv-head-score"
            style={score ? { background: barColor(score) } : undefined}
          >
            {score ? score.toFixed(2) : "—"}
            <span className="rv-head-score-sub">/5</span>
          </span>
          <button className="rv-close" onClick={onClose} aria-label="Close" type="button" disabled={saving}>
            <X size={18} />
          </button>
        </div>

        <div className="rv-body" ref={bodyRef}>
          {/* Meta fields */}
          <div className="rv-meta">
            {cfg.metaFields.map((mf) => (
              <div className={`rv-field ${mf.type === "textarea" ? "rv-field--wide" : ""}`} key={mf.key}>
                <label htmlFor={`meta-${mf.key}`}>{mf.label}</label>
                {mf.type === "textarea" ? (
                  <textarea
                    id={`meta-${mf.key}`}
                    rows={2}
                    value={meta[mf.key] ?? ""}
                    onChange={(e) => setMeta((m) => ({ ...m, [mf.key]: e.target.value }))}
                  />
                ) : (
                  <input
                    id={`meta-${mf.key}`}
                    type={mf.type === "date" ? "date" : "text"}
                    value={meta[mf.key] ?? ""}
                    onChange={(e) => setMeta((m) => ({ ...m, [mf.key]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Pillars + questions */}
          {cfg.pillars.map((p) => (
            <section className="rv-pillar-block" key={p.key}>
              <header className="rv-pillar-head">
                <h4>{p.label}</h4>
                <span className="rv-weight-badge">{Math.round(p.weight * 100)}% weight</span>
              </header>
              {p.questions.map((q) => {
                const cur = answers[q.key]?.score ?? 0;
                const missing = showErrors && !cur;
                return (
                  <div className={`q-block ${missing ? "v-error" : ""}`} key={q.key}>
                    <div className="q-label">
                      {q.label}
                      <span className="req">*</span>
                    </div>
                    <p className="q-prompt">{q.prompt}</p>
                    {q.consider ? (
                      <p className="q-consider">
                        <strong>Consider:</strong> {q.consider}
                      </p>
                    ) : null}
                    <div className="q-opts">
                      {q.options.map((opt, i) => {
                        const val = i + 1;
                        return (
                          <label
                            key={val}
                            className={`q-opt ${cur === val ? "q-opt--active" : ""}`}
                            style={cur === val ? { borderColor: barColor(val), color: barColor(val) } : undefined}
                          >
                            <input
                              type="radio"
                              name={q.key}
                              checked={cur === val}
                              onChange={() => setScore(q.key, val)}
                            />
                            <span className="q-num">{val}</span>
                            <span className="q-lbl">{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                    <details className="q-notes" open={!!answers[q.key]?.note}>
                      <summary>+ Add Notes</summary>
                      <textarea
                        rows={2}
                        placeholder="Optional context…"
                        value={answers[q.key]?.note ?? ""}
                        onChange={(e) => setNote(q.key, e.target.value)}
                      />
                    </details>
                  </div>
                );
              })}
            </section>
          ))}

          {/* Unweighted extras (e.g. R1 Stack Overlap) */}
          {cfg.extras.length > 0 ? (
            <section className="rv-pillar-block rv-pillar-block--extra">
              <header className="rv-pillar-head">
                <h4>Standalone Checks</h4>
                <span className="rv-weight-badge rv-weight-badge--muted">Unweighted</span>
              </header>
              {cfg.extras.map((q) => {
                const cur = answers[q.key]?.score ?? 0;
                return (
                  <div className="q-block q-block--extra" key={q.key}>
                    <div className="q-label">{q.label}</div>
                    <p className="q-prompt">{q.prompt}</p>
                    {q.consider ? (
                      <p className="q-consider">
                        <strong>Consider:</strong> {q.consider}
                      </p>
                    ) : null}
                    <div className="q-opts">
                      {q.options.map((opt, i) => {
                        const val = i + 1;
                        return (
                          <label
                            key={val}
                            className={`q-opt ${cur === val ? "q-opt--active" : ""}`}
                            style={cur === val ? { borderColor: barColor(val), color: barColor(val) } : undefined}
                          >
                            <input
                              type="radio"
                              name={q.key}
                              checked={cur === val}
                              onChange={() => setScore(q.key, val)}
                            />
                            <span className="q-num">{val}</span>
                            <span className="q-lbl">{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </section>
          ) : null}

          {/* Risk flags */}
          {flags.length > 0 ? (
            <div className="rv-flags">
              <div className="rv-flags-head">
                <AlertTriangle size={15} /> Risk Flags
              </div>
              <ul>
                {flags.map((fl) => (
                  <li key={fl.q} className={`rv-flag rv-flag--${fl.level}`}>
                    <span className="rv-flag-q">{fl.q}</span> {fl.label}
                    <span className="rv-flag-level">{fl.level}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {aiError ? (
            <div className="rv-error">
              <strong>AI review unavailable.</strong> {aiError}
            </div>
          ) : null}
          {error ? <div className="rv-error">{error}</div> : null}
        </div>

        {/* Footer */}
        <div className="rv-foot">
          <span className="rv-foot-status">
            {complete ? "All questions answered" : "Complete all required questions to save"}
          </span>
          <div className="rv-foot-actions">
            {showAiReview ? (
              <button
                className="rv-btn rv-btn--ai"
                onClick={handleAiReviewClick}
                type="button"
                disabled={saving || aiReviewing}
              >
                {aiReviewing ? (
                  <>
                    <Loader2 size={15} className="rv-spin" /> Reviewing…
                  </>
                ) : (
                  <>
                    <Sparkles size={15} /> AI Review
                  </>
                )}
              </button>
            ) : null}
            <button className="rv-btn rv-btn--ghost" onClick={onClose} type="button" disabled={saving}>
              Cancel
            </button>
            <button className="rv-btn rv-btn--primary" onClick={handleSave} type="button" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 size={15} className="rv-spin" /> Saving…
                </>
              ) : existing ? (
                "Update Score"
              ) : (
                "Save Review"
              )}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmAiOverwrite}
        title="Replace your answers with an AI review?"
        message="You've already scored some questions by hand. Running AI Review will overwrite every score and note below with the AI's assessment — nothing is saved until you click Save Review."
        confirmLabel="Run AI Review"
        cancelLabel="Keep my answers"
        tone="danger"
        autoFocusConfirm={false}
        onConfirm={() => {
          setConfirmAiOverwrite(false);
          void runAiReview();
        }}
        onCancel={() => setConfirmAiOverwrite(false)}
      />
    </div>
  );
}
